import React, { useState } from "react";
import DeleteButton from "./DeleteButton";

export function ProductImagePreview({ src, alt }) {
  const [position, setPosition] = useState(null);

  const showPreview = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    setPosition({
      left: Math.min(bounds.right + 8, Math.max(8, window.innerWidth - 344)),
      top: Math.max(8, Math.min(bounds.top, window.innerHeight - 344)),
    });
  };

  return (
    <span
      className="inline-flex"
      onMouseEnter={showPreview}
      onMouseLeave={() => setPosition(null)}
    >
      <button
        type="button"
        aria-label={`Preview ${alt}`}
        onFocus={showPreview}
        onBlur={() => setPosition(null)}
        className="rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <img
          src={src}
          alt={alt}
          className="h-8 w-8 rounded border border-gray-300 bg-white object-contain"
        />
      </button>
      {position && (
        <span
          className="pointer-events-none fixed z-50 rounded-lg border border-gray-300 bg-white p-2 shadow-xl"
          style={position}
        >
          <img src={src} alt="" className="h-80 w-80 object-contain" />
        </span>
      )}
    </span>
  );
}

export default function ProductImageManager({
  id,
  images,
  onMove,
  onRemove,
  onFilesSelected,
  inputRef,
  warning,
  disabled = false,
}) {
  return (
    <section
      className="mt-1 border-t border-zinc-200 pt-2"
      aria-labelledby={`${id}-label`}
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3
          id={`${id}-label`}
          className="text-xs font-semibold uppercase tracking-wide text-zinc-800"
        >
          Product images{" "}
          <span className="font-normal normal-case tracking-normal text-zinc-500">
            ({images.length}/5 · first image is main)
          </span>
        </h3>
        {warning && <p className="text-xs text-amber-800">{warning}</p>}
      </div>

      <div className="mb-2 grid min-h-32 grid-cols-1 items-center gap-2 sm:h-32 sm:grid-cols-5">
        {images.map((image, imageIndex) => (
          <div
            key={image.key}
            className={`flex h-28 min-w-0 flex-col items-center justify-between rounded-md border p-1 ${
              imageIndex === 0
                ? "border-blue-300 bg-blue-50/70 ring-1 ring-blue-100"
                : "border-zinc-200 bg-zinc-50"
            }`}
          >
            <div className="flex h-8 w-full items-center justify-center gap-2">
              {image.src ? (
                <ProductImagePreview src={image.src} alt={image.alt} />
              ) : (
                <span className="flex h-8 w-8 items-center justify-center rounded border border-zinc-200 bg-white text-[10px] font-semibold text-zinc-500">
                  PNG
                </span>
              )}
              {imageIndex === 0 && (
                <span className="rounded bg-blue-700 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                  Main image
                </span>
              )}
            </div>
            <div className="min-w-0 w-full text-center">
              <p title={image.name} className="truncate text-xs text-zinc-800">
                {image.name}
              </p>
              <p className="text-[10px] text-zinc-500">{image.status}</p>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-label={`Move ${image.name} earlier`}
                disabled={disabled || imageIndex === 0}
                onClick={() => onMove(imageIndex, -1)}
                className="flex h-7 w-7 items-center justify-center rounded border border-zinc-200 bg-white text-sm text-zinc-600 hover:bg-zinc-100 disabled:opacity-30"
              >
                ↑
              </button>
              <button
                type="button"
                aria-label={`Move ${image.name} later`}
                disabled={disabled || imageIndex === images.length - 1}
                onClick={() => onMove(imageIndex, 1)}
                className="flex h-7 w-7 items-center justify-center rounded border border-zinc-200 bg-white text-sm text-zinc-600 hover:bg-zinc-100 disabled:opacity-30"
              >
                ↓
              </button>
              <DeleteButton
                label={`Remove ${image.name}`}
                title="Remove image"
                disabled={disabled}
                onClick={() => onRemove(image)}
              />
            </div>
          </div>
        ))}
        {!images.length && (
          <p className="text-xs text-zinc-500 sm:col-span-5">
            No images added.
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label
          htmlFor={`${id}-file`}
          className="text-xs font-medium text-zinc-700"
        >
          Add PNG images
        </label>
        <input
          id={`${id}-file`}
          ref={inputRef}
          type="file"
          multiple
          className="min-w-0 max-w-full text-xs file:mr-3 file:rounded-md file:border-0 file:bg-zinc-100 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-zinc-700 hover:file:bg-zinc-200"
          accept="image/png"
          onChange={onFilesSelected}
          disabled={disabled || images.length >= 5}
        />
        <span className="text-xs text-zinc-500">PNG · up to 5 total</span>
      </div>
    </section>
  );
}
